import {
  getIsoDateOrTimestampSec,
  getIsoDateTimestampSec,
  isTimestampSecWithinSupportedRange,
} from "@/lib/TimestampValidation";
import { hasValidUrlHostname, normalizeWebsiteUrlWithDefaultHttps } from "@/lib/websiteUrl";

import { isAdsBillingCountryCode } from "./AdsBillingUtils";
import { sanitizeBudgetInput } from "./AdsManagerUtils";
import {
  CAMPAIGN_BUDGET_MICROS_PER_UNIT,
  getCampaignBudgetMinimumErrorMessage,
  getCampaignBudgetMinimumMicros,
} from "./CampaignBudgetUtils";
import { formatCurrencyAmountInput, isZeroDecimalCurrency } from "./CurrencyDefaults";
import { ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE } from "./GeoTargetingCountries";
import { getLandingPageQueryStringTemplateValidationError } from "./LandingPageQueryStringTemplateUtils";

import type { ISO4217CurrencyCode } from "./AdsManagerUtils";
import type { CampaignBudgetType } from "./CampaignBudgetUtils";
import type { ZeroDecimalCurrencyCodes } from "./CurrencyDefaults";

export type AdsManagerValidationError = string | undefined;
export type AdsManagerValidationErrors<Field extends string> = Partial<Record<Field, string>>;

export type AdsManagerCampaignObjective = "reach" | "clicks" | "conversions";
export type AdsManagerUploadCampaignObjective = "clicks" | "views" | "conversions";
export type AdsManagerCampaignType = "standard" | "product_feed";
export type ProductSetFilterOperator =
  | "in"
  | "not_in"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "gt"
  | "gte"
  | "lt"
  | "lte";

export type ConversionEventSettingValidationInput = {
  eventType: string;
  customEventName: string | null;
  sourceIds: string[];
};

export type ProductSetFilterValidationInput = {
  field: string;
  operator: ProductSetFilterOperator;
  valuesString?: string;
  values?: string[];
};

export type CustomAudienceBidMultiplierValidationInput = {
  customAudienceId: string;
  bidMultiplier: string;
};

export type CampaignValidationField =
  | "campaignName"
  | "objective"
  | "conversionEventSettings"
  | "locationCountryCodes"
  | "queryStringTemplate"
  | "customAudienceIds"
  | "excludedCustomAudienceIds"
  | "budgetAmount"
  | "budgetType"
  | "startDate"
  | "endDate"
  | "targetCountries"
  | "locationIds";

export type CampaignValidationInput = {
  campaignName?: string;
  objective?: AdsManagerCampaignObjective | AdsManagerUploadCampaignObjective | string | null;
  conversionEventSettings?: ConversionEventSettingValidationInput[];
  locationCountryCodes?: string[];
  locationGeoLocations?: unknown[];
  queryStringTemplate?: string;
  customAudienceIds?: string[];
  excludedCustomAudienceIds?: string[];
  budgetType?: CampaignBudgetType | string;
  budgetAmount?: string;
  startDate?: Date | null;
  hasEndDate?: boolean;
  endDate?: Date | null;
  targetCountries?: string;
  locationIds?: string;
};

export type AdGroupValidationField =
  | "name"
  | "campaignId"
  | "websiteUrl"
  | "productFeedId"
  | "productSetFilters"
  | "contextHints"
  | "maxBidAmount"
  | "customAudienceBidMultipliers";

export type AdGroupValidationInput = {
  name?: string;
  campaignId?: string | null;
  websiteUrl?: string;
  productFeedId?: string;
  productSetFilters?: ProductSetFilterValidationInput[];
  contextHints?: string;
  maxBidAmount?: string;
  biddingType?: "click" | "impression";
  customAudienceBidMultipliers?: CustomAudienceBidMultiplierValidationInput[];
};

export type AdValidationField =
  | "name"
  | "title"
  | "body"
  | "targetUrl"
  | "image"
  | "imageUrl"
  | "imageFileId"
  | "adGroupId";

export type AdValidationInput = {
  name?: string;
  title?: string;
  body?: string;
  targetUrl?: string;
  image?: { file?: { size: number; name?: string; type?: string } } | null;
  imageUrl?: string;
  imageFileId?: string;
  adGroupId?: string | null;
};

export type AdsManagerFullValidationMode = "create" | "edit";
export type AdsManagerPartialValidationMode = "bulkEdit" | "upload";
export type AdsManagerValidationMode =
  | AdsManagerFullValidationMode
  | AdsManagerPartialValidationMode;
export type AdsManagerValidationFieldSelection<Field extends string> = readonly Field[] | "all";

type FullValidationOptions<Field extends string> = {
  mode?: AdsManagerFullValidationMode;
  fields?: AdsManagerValidationFieldSelection<Field>;
};

type BulkEditValidationOptions<Field extends string> = {
  mode: "bulkEdit";
  fields: AdsManagerValidationFieldSelection<Field>;
};

type UploadValidationOptions<Field extends string> = {
  mode: "upload";
  fields: AdsManagerValidationFieldSelection<Field>;
};

type ValidationModeOptions<Field extends string> =
  | FullValidationOptions<Field>
  | BulkEditValidationOptions<Field>
  | UploadValidationOptions<Field>;

export type CampaignValidationOptions = ValidationModeOptions<CampaignValidationField> & {
  now?: Date;
  originalCampaignName?: string | null;
  allowUnchangedLegacyName?: boolean;
  minDailyCampaignBudgetMicros?: number;
  minLifetimeCampaignBudgetMicros?: number;
  isOpprefMacroEnabled?: boolean;
};

type AdGroupBaseValidationOptions = {
  accountCurrencyCode: ISO4217CurrencyCode;
  originalAdGroupName?: string | null;
  allowUnchangedLegacyName?: boolean;
  isCreation?: boolean;
  availableCampaignIds?: string[];
  isProductFeedCampaign?: boolean;
  campaignObjective?: AdsManagerCampaignObjective;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
};

export type AdGroupValidationOptions = AdGroupBaseValidationOptions &
  (
    | FullValidationOptions<AdGroupValidationField>
    | BulkEditValidationOptions<AdGroupValidationField>
    | (UploadValidationOptions<AdGroupValidationField> & {
        uploadCampaignObjective: AdsManagerUploadCampaignObjective;
      })
  );

export type AdValidationOptions = ValidationModeOptions<AdValidationField> & {
  originalAdName?: string | null;
  originalAdTitle?: string | null;
  allowUnchangedLegacyName?: boolean;
  allowUnchangedLegacyTitle?: boolean;
  isProductFeedCreative?: boolean;
  isCreation?: boolean;
  availableAdGroupIds?: string[];
  requireImage?: boolean;
};

const MICROS_PER_CURRENCY_UNIT = 1_000_000;
const IMPRESSIONS_PER_CPM = 1_000;
const CLICK_MAX_BID_MICROS_MIN = 10_000;
const CLICK_MAX_BID_MICROS_MAX = 100 * MICROS_PER_CURRENCY_UNIT;
const CLICK_MAX_BID_MICROS_MAX_JPY = 16_000 * MICROS_PER_CURRENCY_UNIT;
const CLICK_MAX_BID_MICROS_MAX_KRW = 152_000 * MICROS_PER_CURRENCY_UNIT;
const CPM_BID_MICROS_MIN = 10_000;
const CPM_BID_MICROS_MAX = 100 * MICROS_PER_CURRENCY_UNIT * IMPRESSIONS_PER_CPM;

export const ADS_MANAGER_ENTITY_NAME_MIN_LENGTH = 3;
export const ADS_MANAGER_ENTITY_NAME_MAX_LENGTH = 1_000;
export const ADS_MANAGER_CONTEXT_HINTS_MAX_LENGTH = 10_000;
export const ADS_MANAGER_WEBSITE_URL_MAX_LENGTH = 1_000;
const ADS_MANAGER_AD_DESTINATION_URL_MAX_LENGTH = 2_048;
export const ADS_MANAGER_MAX_CAMPAIGN_BUDGET_AMOUNT = 1_000_000_000_000;
export const ADS_MANAGER_AD_HEADLINE_MAX_LENGTH = 50;
export const ADS_MANAGER_AD_DESCRIPTION_MAX_LENGTH = 100;
export const ADS_MANAGER_PRODUCT_FEED_AD_HEADLINE_MAX_LENGTH = 30;
export const ADS_MANAGER_AD_IMAGE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export const ADS_MANAGER_BID_MULTIPLIER_MICROS_PER_UNIT = 1_000_000;
export const ADS_MANAGER_MIN_BID_MULTIPLIER_MICROS = 100_000;
export const ADS_MANAGER_MAX_BID_MULTIPLIER_MICROS = 10_000_000;

export const ADS_MANAGER_CLICK_MAX_BID_MICROS_MAX_BY_CURRENCY: Record<string, number | undefined> =
  {
    USD: CLICK_MAX_BID_MICROS_MAX,
    AUD: CLICK_MAX_BID_MICROS_MAX,
    NZD: CLICK_MAX_BID_MICROS_MAX,
    CAD: CLICK_MAX_BID_MICROS_MAX,
    GBP: CLICK_MAX_BID_MICROS_MAX,
    KRW: CLICK_MAX_BID_MICROS_MAX_KRW,
    JPY: CLICK_MAX_BID_MICROS_MAX_JPY,
    BRL: CLICK_MAX_BID_MICROS_MAX,
    MXN: CLICK_MAX_BID_MICROS_MAX,
  };

const NUMERIC_PRODUCT_SET_FILTER_OPERATORS = new Set<ProductSetFilterOperator>([
  "gt",
  "gte",
  "lt",
  "lte",
]);
const SINGLE_TEXT_PRODUCT_SET_FILTER_OPERATORS = new Set<ProductSetFilterOperator>([
  "contains",
  "not_contains",
  "starts_with",
]);
const RESERVED_AD_DESTINATION_QUERY_PARAM_NAMES = new Set(["olref", "oppref"]);
const TARGET_COUNTRIES_JSON_ERROR =
  'Target countries must be a JSON array of ISO 3166-1 alpha-2 country codes like ["US", "CA"].';
const JSON_STRING_ARRAY_ERROR = "This field must be a JSON array of strings.";
export const UPLOAD_CAMPAIGN_OBJECTIVE_ERROR =
  'Campaign objective must be "clicks", "views", or "conversions".';
export const UPLOAD_CONVERSION_CAMPAIGN_DISABLED_ERROR =
  "Conversion campaign bulk uploads are not enabled for this ad account.";
const DATE_RANGE_ERROR = "This field must be between Jan 1, 2000 and Jan 1, 2100.";
const ISO_DATE_ERROR = "This field must be a valid date in YYYY-MM-DD format.";
const ISO_DATE_OR_TIMESTAMP_ERROR =
  "This field must be a valid date in YYYY-MM-DD format or a timezone-aware ISO timestamp.";
const GIF_AD_IMAGE_ERROR = "GIF images are not supported. Upload a PNG or JPG image.";

const CAMPAIGN_FULL_VALIDATION_FIELDS: readonly CampaignValidationField[] = [
  "campaignName",
  "objective",
  "conversionEventSettings",
  "locationCountryCodes",
  "queryStringTemplate",
  "customAudienceIds",
  "excludedCustomAudienceIds",
  "budgetType",
  "budgetAmount",
  "startDate",
  "endDate",
  "targetCountries",
  "locationIds",
];

const AD_GROUP_FULL_VALIDATION_FIELDS: readonly AdGroupValidationField[] = [
  "name",
  "campaignId",
  "maxBidAmount",
  "contextHints",
  "websiteUrl",
  "customAudienceBidMultipliers",
  "productFeedId",
  "productSetFilters",
];

const AD_FULL_VALIDATION_FIELDS: readonly AdValidationField[] = [
  "name",
  "title",
  "body",
  "targetUrl",
  "image",
  "imageUrl",
  "imageFileId",
  "adGroupId",
];

export function validateCampaign(
  campaign: CampaignValidationInput,
  options: CampaignValidationOptions = {},
): AdsManagerValidationErrors<CampaignValidationField> {
  const errors: AdsManagerValidationErrors<CampaignValidationField> = {};
  const selectedFields = getSelectedValidationFields(CAMPAIGN_FULL_VALIDATION_FIELDS, options);
  const mode = getValidationMode(options);
  const budgetType = normalizeCampaignBudgetType(campaign.budgetType);

  if (selectedFields.has("campaignName")) {
    addError(
      errors,
      "campaignName",
      validateCampaignName(campaign.campaignName ?? "", {
        originalValue: options.originalCampaignName,
        allowUnchangedLegacyValue: options.allowUnchangedLegacyName,
      }),
    );
  }
  if (selectedFields.has("objective")) {
    addError(
      errors,
      "objective",
      mode === "upload" ?
        validateUploadCampaignObjective(campaign.objective)
      : validateCampaignObjective(campaign.objective),
    );
  }
  if (selectedFields.has("conversionEventSettings")) {
    addError(
      errors,
      "conversionEventSettings",
      validateCampaignConversionEventSettings({
        objective: getCampaignObjectiveForFormValidation(campaign.objective),
        settings: campaign.conversionEventSettings ?? [],
      }),
    );
  }
  if (selectedFields.has("locationCountryCodes")) {
    addError(
      errors,
      "locationCountryCodes",
      validateCampaignLocationSelection({
        locationCountryCodes: campaign.locationCountryCodes ?? [],
        locationGeoLocations: campaign.locationGeoLocations ?? [],
      }),
    );
  }
  if (selectedFields.has("queryStringTemplate")) {
    addError(
      errors,
      "queryStringTemplate",
      validateCampaignQueryStringTemplate(campaign.queryStringTemplate ?? "", {
        isOpprefMacroEnabled: options.isOpprefMacroEnabled,
      }),
    );
  }
  if (selectedFields.has("customAudienceIds") || selectedFields.has("excludedCustomAudienceIds")) {
    addError(
      errors,
      selectedFields.has("excludedCustomAudienceIds") ?
        "excludedCustomAudienceIds"
      : "customAudienceIds",
      validateCustomAudienceTargeting({
        customAudienceIds: campaign.customAudienceIds ?? [],
        excludedCustomAudienceIds: campaign.excludedCustomAudienceIds ?? [],
      }),
    );
  }
  if (selectedFields.has("budgetType")) {
    addError(errors, "budgetType", validateCampaignBudgetType(campaign.budgetType));
  }
  if (selectedFields.has("budgetAmount")) {
    addError(
      errors,
      "budgetAmount",
      validateCampaignBudgetAmount({
        amount: campaign.budgetAmount ?? "",
        budgetType,
        allowFormattedInput: mode !== "upload",
        minDailyCampaignBudgetMicros: options.minDailyCampaignBudgetMicros,
        minLifetimeCampaignBudgetMicros: options.minLifetimeCampaignBudgetMicros,
      }),
    );
  }
  if (selectedFields.has("startDate")) {
    addError(
      errors,
      "startDate",
      validateCampaignStartDate(campaign.startDate, { mode, now: options.now }),
    );
  }
  if (selectedFields.has("endDate")) {
    addError(
      errors,
      "endDate",
      validateCampaignEndDate({
        hasEndDate: campaign.hasEndDate === true,
        endDate: campaign.endDate,
        startDate: campaign.startDate,
        mode,
        now: options.now,
      }),
    );
  }
  if (selectedFields.has("targetCountries")) {
    addError(errors, "targetCountries", validateTargetCountriesString(campaign.targetCountries));
  }
  if (selectedFields.has("locationIds")) {
    addError(errors, "locationIds", validateJsonStringArray(campaign.locationIds));
  }

  return errors;
}

export function validateCampaignInputs(
  campaign: CampaignValidationInput,
  options: CampaignValidationOptions = {},
): AdsManagerValidationErrors<CampaignValidationField> {
  return validateCampaign(campaign, options);
}

export function validateAdGroup(
  adGroup: AdGroupValidationInput,
  options: AdGroupValidationOptions,
): AdsManagerValidationErrors<AdGroupValidationField> {
  const errors: AdsManagerValidationErrors<AdGroupValidationField> = {};
  const selectedFields = getSelectedValidationFields(AD_GROUP_FULL_VALIDATION_FIELDS, options);
  const isCreation = isCreationValidation(options);
  const isProductFeedCampaign = options.isProductFeedCampaign === true;
  const biddingType = getAdGroupBiddingType({
    biddingType: adGroup.biddingType,
    campaignObjective: options.campaignObjective,
  });

  if (selectedFields.has("name")) {
    addError(
      errors,
      "name",
      validateAdGroupName(adGroup.name ?? "", {
        originalValue: options.originalAdGroupName,
        allowUnchangedLegacyValue: options.allowUnchangedLegacyName,
      }),
    );
  }
  if (isCreation && selectedFields.has("campaignId")) {
    addError(
      errors,
      "campaignId",
      validateParentSelection({
        value: adGroup.campaignId,
        availableValues: options.availableCampaignIds,
        missingMessage: "Select a campaign.",
        unavailableMessage: "Select an available campaign.",
      }),
    );
  }
  if (selectedFields.has("maxBidAmount")) {
    addError(
      errors,
      "maxBidAmount",
      options.mode === "upload" ?
        validateMaxBidForUploadCampaignObjective({
          maxBid: adGroup.maxBidAmount,
          objective: options.uploadCampaignObjective,
        })
      : biddingType == null ? "Select a bidding type before entering a max bid."
      : validateAdGroupMaxBidAmount({
          amount: adGroup.maxBidAmount,
          biddingType,
          accountCurrencyCode: options.accountCurrencyCode,
          zeroDecimalCurrencyCodes: options.zeroDecimalCurrencyCodes,
        }),
    );
  }
  if (!isProductFeedCampaign && selectedFields.has("contextHints")) {
    addError(
      errors,
      "contextHints",
      options.mode === "upload" ?
        validateAdGroupContextHintsForUpload(adGroup.contextHints)
      : validateAdGroupContextHints(adGroup.contextHints ?? ""),
    );
  }
  if (!isProductFeedCampaign && selectedFields.has("websiteUrl")) {
    addError(errors, "websiteUrl", validateOptionalWebsiteUrl(adGroup.websiteUrl ?? ""));
  }
  if (selectedFields.has("customAudienceBidMultipliers")) {
    addError(
      errors,
      "customAudienceBidMultipliers",
      validateCustomAudienceBidMultipliers(adGroup.customAudienceBidMultipliers ?? []),
    );
  }

  if (isProductFeedCampaign) {
    if (selectedFields.has("productFeedId")) {
      addError(errors, "productFeedId", validateProductFeedId(adGroup.productFeedId ?? ""));
    }
    if (selectedFields.has("productSetFilters")) {
      addError(
        errors,
        "productSetFilters",
        validateProductSetFilters(adGroup.productSetFilters ?? []),
      );
    }
  }

  return errors;
}

export function validateAdGroupInputs(
  adGroup: AdGroupValidationInput,
  options: AdGroupValidationOptions,
): AdsManagerValidationErrors<AdGroupValidationField> {
  return validateAdGroup(adGroup, options);
}

export function validateAd(
  ad: AdValidationInput,
  options: AdValidationOptions = {},
): AdsManagerValidationErrors<AdValidationField> {
  const errors: AdsManagerValidationErrors<AdValidationField> = {};
  const selectedFields = getSelectedValidationFields(AD_FULL_VALIDATION_FIELDS, options);
  const isProductFeedCreative = options.isProductFeedCreative === true;
  const isCreation = isCreationValidation(options);

  if (selectedFields.has("name")) {
    addError(
      errors,
      "name",
      validateAdName(ad.name ?? "", {
        originalValue: options.originalAdName,
        allowUnchangedLegacyValue: options.allowUnchangedLegacyName,
      }),
    );
  }
  if (selectedFields.has("title")) {
    addError(
      errors,
      "title",
      validateAdTitle(ad.title ?? "", {
        originalValue: options.originalAdTitle,
        allowUnchangedLegacyValue: options.allowUnchangedLegacyTitle,
        isProductFeedCreative,
      }),
    );
  }

  if (!isProductFeedCreative) {
    if (selectedFields.has("body")) {
      addError(
        errors,
        "body",
        validateAdDescription(ad.body ?? "", { requireMinimumLength: true }),
      );
    }
    if (selectedFields.has("targetUrl")) {
      addError(
        errors,
        "targetUrl",
        validateAdDestinationUrl(ad.targetUrl ?? "", { required: true }),
      );
    }
    if (selectedFields.has("image")) {
      addError(
        errors,
        "image",
        validateAdImageRequirement({
          image: ad.image,
          imageUrl: ad.imageUrl,
          imageFileId: ad.imageFileId,
          required: options.requireImage === true || isCreation,
        }),
      );
    }
    if (selectedFields.has("imageUrl")) {
      addError(errors, "imageUrl", validateAdImageUrl(ad.imageUrl ?? ""));
    }
    if (selectedFields.has("imageFileId")) {
      addError(
        errors,
        "imageFileId",
        validateAdImageSource({ imageUrl: ad.imageUrl, imageFileId: ad.imageFileId }),
      );
    }
  }

  if (isCreation && selectedFields.has("adGroupId")) {
    addError(
      errors,
      "adGroupId",
      validateParentSelection({
        value: ad.adGroupId,
        availableValues: options.availableAdGroupIds,
        missingMessage: "Select an ad group.",
        unavailableMessage: "Select an available ad group.",
      }),
    );
  }

  return errors;
}

export function validateAdInputs(
  ad: AdValidationInput,
  options: AdValidationOptions = {},
): AdsManagerValidationErrors<AdValidationField> {
  return validateAd(ad, options);
}

export function validateCampaignName(
  value: string,
  options: LegacyValueValidationOptions = {},
): AdsManagerValidationError {
  return validateEntityName(value, "campaign name", options);
}

export function validateAdGroupName(
  value: string,
  options: LegacyValueValidationOptions = {},
): AdsManagerValidationError {
  return validateEntityName(value, "ad group name", options);
}

export function validateAdName(
  value: string,
  options: LegacyValueValidationOptions = {},
): AdsManagerValidationError {
  return validateEntityName(value, "ad name", options);
}

export function validateEntityName(
  value: string,
  entityLabel: string,
  options: LegacyValueValidationOptions = {},
): AdsManagerValidationError {
  const trimmedValue = value.trim();
  const article = getIndefiniteArticle(entityLabel);
  if (!trimmedValue) {
    return `Enter ${article} ${entityLabel}.`;
  }
  if (
    trimmedValue.length < ADS_MANAGER_ENTITY_NAME_MIN_LENGTH &&
    !isAllowedUnchangedLegacyValue(trimmedValue, options)
  ) {
    return `Enter ${article} ${entityLabel} with at least ${ADS_MANAGER_ENTITY_NAME_MIN_LENGTH} characters.`;
  }
  if (value.length > ADS_MANAGER_ENTITY_NAME_MAX_LENGTH) {
    return `Enter ${article} ${entityLabel} with no more than ${ADS_MANAGER_ENTITY_NAME_MAX_LENGTH} characters.`;
  }
  return undefined;
}

type LegacyValueValidationOptions = {
  originalValue?: string | null;
  allowUnchangedLegacyValue?: boolean;
};

function isAllowedUnchangedLegacyValue(
  trimmedValue: string,
  options: LegacyValueValidationOptions,
): boolean {
  return (
    options.allowUnchangedLegacyValue === true &&
    options.originalValue != null &&
    trimmedValue === options.originalValue.trim()
  );
}

export function validateCampaignObjective(
  objective: string | null | undefined,
): AdsManagerValidationError {
  if (objective == null) {
    return "Choose an objective.";
  }
  if (!["reach", "clicks", "conversions"].includes(objective)) {
    return "Choose an objective.";
  }
  return undefined;
}

export function validateUploadCampaignObjective(
  value: string | null | undefined,
): AdsManagerValidationError {
  const objective = normalizeUploadCampaignObjective(value);
  if (objective == null) {
    if (value == null || value.trim().length === 0) {
      return undefined;
    }
    return UPLOAD_CAMPAIGN_OBJECTIVE_ERROR;
  }
  return undefined;
}

export function normalizeUploadCampaignObjective(
  value: string | null | undefined,
): AdsManagerUploadCampaignObjective | null {
  const normalizedValue = value?.trim().toLowerCase();
  if (normalizedValue == null || normalizedValue.length === 0) {
    return null;
  }
  if (
    normalizedValue !== "clicks" &&
    normalizedValue !== "views" &&
    normalizedValue !== "conversions"
  ) {
    return null;
  }
  return normalizedValue;
}

export function isClickUploadCampaignObjective(
  objective: AdsManagerUploadCampaignObjective,
): objective is "clicks" | "conversions" {
  return objective === "clicks" || objective === "conversions";
}

export function validateConversionCampaignBulkUploadEnabled({
  objective,
  enableConversionCampaigns,
}: {
  objective: AdsManagerUploadCampaignObjective | string | null | undefined;
  enableConversionCampaigns: boolean;
}): AdsManagerValidationError {
  if (enableConversionCampaigns) {
    return undefined;
  }
  return normalizeUploadCampaignObjective(objective) === "conversions" ?
      UPLOAD_CONVERSION_CAMPAIGN_DISABLED_ERROR
    : undefined;
}

export function validateConversionEventSettingIdsForUploadCampaignObjective({
  objective,
  conversionEventSettingIds,
}: {
  objective: AdsManagerUploadCampaignObjective;
  conversionEventSettingIds: string | undefined;
}): AdsManagerValidationError {
  if (objective !== "conversions") {
    return undefined;
  }
  const jsonError = validateJsonStringArray(conversionEventSettingIds);
  if (jsonError != null) {
    return jsonError;
  }
  let parsedValue: unknown;
  try {
    parsedValue = JSON.parse(normalizeSmartDoubleQuotes(conversionEventSettingIds ?? ""));
  } catch {
    return "Conversion campaigns must set exactly one conversion_event_setting_ids value.";
  }
  if (
    !Array.isArray(parsedValue) ||
    parsedValue.length !== 1 ||
    typeof parsedValue[0] !== "string" ||
    parsedValue[0].trim().length === 0
  ) {
    return "Conversion campaigns must set exactly one conversion_event_setting_ids value.";
  }
  return undefined;
}

export function isClickCampaignObjective(
  objective: AdsManagerCampaignObjective | null | undefined,
): objective is "clicks" | "conversions" {
  return objective === "clicks" || objective === "conversions";
}

function getAdGroupBiddingType({
  biddingType,
  campaignObjective,
}: {
  biddingType: AdGroupValidationInput["biddingType"];
  campaignObjective: AdsManagerCampaignObjective | undefined;
}): NonNullable<AdGroupValidationInput["biddingType"]> | null {
  if (biddingType != null) {
    return biddingType;
  }
  if (campaignObjective == null) {
    return null;
  }
  return isClickCampaignObjective(campaignObjective) ? "click" : "impression";
}

function getCampaignObjectiveForFormValidation(
  objective: CampaignValidationInput["objective"],
): AdsManagerCampaignObjective | null {
  return objective === "reach" || objective === "clicks" || objective === "conversions" ?
      objective
    : null;
}

export function validateCampaignConversionEventSettings({
  objective,
  settings,
}: {
  objective: AdsManagerCampaignObjective | null;
  settings: ConversionEventSettingValidationInput[];
}): AdsManagerValidationError {
  if (objective === "conversions" && settings.length === 0) {
    return "Select one conversion event for the conversion objective.";
  }
  if (objective === "conversions" && settings.length > 1) {
    return "Select only one conversion event for the conversion objective.";
  }
  if (objective === "conversions" && settings.some((setting) => setting.eventType === "custom")) {
    return "Select a standard conversion event for the conversion objective.";
  }
  if (hasDuplicateConversionEventSettingSelections(settings)) {
    return "Remove duplicate conversion events using the same data source and event name.";
  }
  return undefined;
}

export function hasDuplicateConversionEventSettingSelections(
  settings: ConversionEventSettingValidationInput[],
): boolean {
  const seenKeys = new Set<string>();
  for (const setting of settings) {
    const deduplicationKey = getConversionEventSettingDeduplicationKey(setting);
    if (seenKeys.has(deduplicationKey)) {
      return true;
    }
    seenKeys.add(deduplicationKey);
  }
  return false;
}

export function getConversionEventSettingDeduplicationKey({
  eventType,
  customEventName,
  sourceIds,
}: ConversionEventSettingValidationInput): string {
  const sourceKey = sourceIds
    .map((sourceId) => sourceId.trim())
    .filter(Boolean)
    .sort()
    .join(",");
  const eventKey = eventType === "custom" ? customEventName?.trim() ?? "" : eventType.trim();
  return `${sourceKey}:${eventKey}`;
}

export function validateCampaignLocationSelection({
  locationCountryCodes,
  locationGeoLocations,
}: {
  locationCountryCodes: string[];
  locationGeoLocations: unknown[];
}): AdsManagerValidationError {
  if (locationCountryCodes.length === 0 && locationGeoLocations.length === 0) {
    return "Select at least one location.";
  }
  return undefined;
}

export function validateCampaignQueryStringTemplate(
  value: string,
  options: { isOpprefMacroEnabled?: boolean } = {},
): AdsManagerValidationError {
  return getLandingPageQueryStringTemplateValidationError(value, options);
}

export function validateCustomAudienceTargeting({
  customAudienceIds,
  excludedCustomAudienceIds,
}: {
  customAudienceIds: string[];
  excludedCustomAudienceIds: string[];
}): AdsManagerValidationError {
  if (excludedCustomAudienceIds.some((excludedId) => customAudienceIds.includes(excludedId))) {
    return "Remove audiences already selected as include filters.";
  }
  return undefined;
}

export function validateCampaignBudgetType(
  value: string | null | undefined,
): AdsManagerValidationError {
  if (value == null || String(value).trim().length === 0) {
    return undefined;
  }
  const normalizedValue = String(value).trim().toLowerCase();
  if (normalizedValue !== "daily" && normalizedValue !== "lifetime") {
    return 'Campaign budget type must be either "daily" or "lifetime".';
  }
  return undefined;
}

export function normalizeCampaignBudgetType(value: string | null | undefined): CampaignBudgetType {
  return String(value).trim().toLowerCase() === "daily" ? "daily" : "lifetime";
}

export function validateCampaignBudgetAmount({
  amount,
  budgetType,
  allowFormattedInput = true,
  minDailyCampaignBudgetMicros,
  minLifetimeCampaignBudgetMicros,
}: {
  amount: string;
  budgetType: CampaignBudgetType;
  allowFormattedInput?: boolean;
  minDailyCampaignBudgetMicros?: number;
  minLifetimeCampaignBudgetMicros?: number;
}): AdsManagerValidationError {
  const trimmedAmount = (allowFormattedInput ? sanitizeBudgetInput(amount) : amount).trim();
  if (!trimmedAmount) {
    return "Enter a budget amount.";
  }
  if (!allowFormattedInput) {
    const numericError = validateRequiredNumericString(trimmedAmount);
    if (numericError != null) {
      return numericError;
    }
  }

  const budgetAmount = Number(trimmedAmount);
  if (!Number.isFinite(budgetAmount)) {
    return "Enter a valid numeric budget.";
  }
  if (budgetAmount <= 0) {
    return "Enter a budget greater than zero.";
  }

  const budgetMicros = Math.round(budgetAmount * CAMPAIGN_BUDGET_MICROS_PER_UNIT);
  if (
    budgetMicros <
    getCampaignBudgetMinimumMicros(
      budgetType,
      minDailyCampaignBudgetMicros,
      minLifetimeCampaignBudgetMicros,
    )
  ) {
    return getCampaignBudgetMinimumErrorMessage(
      budgetType,
      undefined,
      minDailyCampaignBudgetMicros,
      minLifetimeCampaignBudgetMicros,
    );
  }
  if (budgetAmount > ADS_MANAGER_MAX_CAMPAIGN_BUDGET_AMOUNT) {
    return `Enter a budget of no more than ${ADS_MANAGER_MAX_CAMPAIGN_BUDGET_AMOUNT}.`;
  }
  return undefined;
}

export function validateCampaignStartDate(
  startDate: Date | null | undefined,
  {
    mode = "edit",
    now = new Date(),
  }: {
    mode?: AdsManagerValidationMode;
    now?: Date;
  } = {},
): AdsManagerValidationError {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
    return "Select a start date.";
  }

  const startTimeMs = startDate.getTime();
  if (!isTimestampSecWithinSupportedRange(startTimeMs / 1_000)) {
    return "Select a start date between Jan 1, 2000 and Jan 1, 2100.";
  }
  if (mode === "create" && startTimeMs < now.getTime() - 24 * 60 * 60 * 1_000) {
    return "Start date cannot be in the past.";
  }
  if (mode === "bulkEdit" && startTimeMs <= now.getTime()) {
    return "Start date must be in the future.";
  }
  return undefined;
}

export function validateCampaignEndDate({
  hasEndDate,
  endDate,
  startDate,
  mode = "edit",
  now = new Date(),
}: {
  hasEndDate: boolean;
  endDate: Date | null | undefined;
  startDate: Date | null | undefined;
  mode?: AdsManagerValidationMode;
  now?: Date;
}): AdsManagerValidationError {
  if (!hasEndDate) {
    return undefined;
  }

  if (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())) {
    return "Select an end date.";
  }
  if (!isTimestampSecWithinSupportedRange(endDate.getTime() / 1_000)) {
    return "Select an end date between Jan 1, 2000 and Jan 1, 2100.";
  }

  if (mode === "bulkEdit" && endDate.getTime() <= now.getTime()) {
    return "End date must be in the future.";
  }

  const createModeDateError =
    mode === "create" && endDate.getTime() < now.getTime() ?
      "End date cannot be in the past."
    : undefined;
  if (startDate instanceof Date && !Number.isNaN(startDate.getTime()) && endDate <= startDate) {
    return "End date must be after the start date.";
  }
  return createModeDateError;
}

export function validateIsoDateString(value: string | undefined): AdsManagerValidationError {
  if (value == null || value.trim().length === 0) {
    return undefined;
  }
  const timestampSec = getIsoDateTimestampSec(value.trim());
  if (timestampSec == null) {
    return ISO_DATE_ERROR;
  }
  if (!isTimestampSecWithinSupportedRange(timestampSec)) {
    return DATE_RANGE_ERROR;
  }
  return undefined;
}

export function validateIsoDateOrTimestampString(
  value: string | undefined,
): AdsManagerValidationError {
  if (value == null || value.trim().length === 0) {
    return undefined;
  }
  const timestampSec = getIsoDateOrTimestampSec(value.trim());
  if (timestampSec == null) {
    return ISO_DATE_OR_TIMESTAMP_ERROR;
  }
  if (!isTimestampSecWithinSupportedRange(timestampSec)) {
    return DATE_RANGE_ERROR;
  }
  return undefined;
}

export function validateAdGroupMaxBidAmount({
  amount,
  biddingType,
  accountCurrencyCode,
  zeroDecimalCurrencyCodes,
}: {
  amount: string | undefined;
  biddingType: "click" | "impression";
  accountCurrencyCode: ISO4217CurrencyCode;
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes;
}): AdsManagerValidationError {
  return biddingType === "click" ?
      validateClickMaxBidAmount(amount, accountCurrencyCode, zeroDecimalCurrencyCodes)
    : validateImpressionMaxBidAmount(amount, accountCurrencyCode, zeroDecimalCurrencyCodes);
}

export function validateClickMaxBidAmount(
  amount: string | undefined,
  currency: ISO4217CurrencyCode,
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes,
): AdsManagerValidationError {
  const minMicros = CLICK_MAX_BID_MICROS_MIN;
  const maxMicros = ADS_MANAGER_CLICK_MAX_BID_MICROS_MAX_BY_CURRENCY[currency];
  if (maxMicros == null) {
    return "Unsupported currency for bid validation.";
  }

  const minAmount =
    isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) ?
      Math.ceil(minMicros / MICROS_PER_CURRENCY_UNIT)
    : minMicros / MICROS_PER_CURRENCY_UNIT;
  const maxAmount = maxMicros / MICROS_PER_CURRENCY_UNIT;
  const displayMinAmount = formatCurrencyAmountInput({
    amount: minAmount,
    currency,
    zeroDecimalCurrencyCodes,
  });
  const displayMaxAmount = formatCurrencyAmountInput({
    amount: maxAmount,
    currency,
    zeroDecimalCurrencyCodes,
  });
  const trimmedAmount = amount?.trim() ?? "";
  if (!trimmedAmount) {
    return `Enter a max bid between ${displayMinAmount} and ${displayMaxAmount}.`;
  }

  const parsedAmount = Number(trimmedAmount);
  if (!Number.isFinite(parsedAmount)) {
    return "Enter a valid numeric max bid.";
  }
  if (
    isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) &&
    !Number.isInteger(parsedAmount)
  ) {
    return `Enter a whole amount for ${currency}.`;
  }
  if (parsedAmount < minAmount || parsedAmount > maxAmount) {
    return `Enter a max bid between ${displayMinAmount} and ${displayMaxAmount}.`;
  }
  return undefined;
}

export function validateImpressionMaxBidAmount(
  amount: string | undefined,
  currency?: ISO4217CurrencyCode,
  zeroDecimalCurrencyCodes?: ZeroDecimalCurrencyCodes,
): AdsManagerValidationError {
  const cpmBidMin =
    currency != null && isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) ?
      Math.ceil(CPM_BID_MICROS_MIN / MICROS_PER_CURRENCY_UNIT)
    : CPM_BID_MICROS_MIN / MICROS_PER_CURRENCY_UNIT;
  const cpmBidMax = CPM_BID_MICROS_MAX / MICROS_PER_CURRENCY_UNIT;
  const displayMinAmount =
    currency == null ?
      cpmBidMin.toFixed(2)
    : formatCurrencyAmountInput({ amount: cpmBidMin, currency, zeroDecimalCurrencyCodes });
  const displayMaxAmount =
    currency == null ?
      cpmBidMax.toFixed(2)
    : formatCurrencyAmountInput({ amount: cpmBidMax, currency, zeroDecimalCurrencyCodes });
  const trimmedAmount = amount?.trim() ?? "";
  if (!trimmedAmount) {
    return `Enter a CPM bid between ${displayMinAmount} and ${displayMaxAmount}.`;
  }

  const parsedAmount = Number(trimmedAmount);
  if (!Number.isFinite(parsedAmount)) {
    return "Enter a valid numeric CPM bid.";
  }
  if (
    currency != null &&
    isZeroDecimalCurrency(currency, zeroDecimalCurrencyCodes) &&
    !Number.isInteger(parsedAmount)
  ) {
    return `Enter a whole amount for ${currency}.`;
  }
  if (parsedAmount < cpmBidMin || parsedAmount > cpmBidMax) {
    return `Enter a CPM bid between ${displayMinAmount} and ${displayMaxAmount}.`;
  }
  return undefined;
}

export function validateMaxBidForUploadCampaignObjective({
  maxBid,
  objective,
}: {
  maxBid: string | undefined;
  objective: AdsManagerUploadCampaignObjective;
}): AdsManagerValidationError {
  const trimmedMaxBid = maxBid?.trim() ?? "";
  if (trimmedMaxBid) {
    const numericError = validateRequiredNumericString(trimmedMaxBid);
    if (numericError != null) {
      return numericError;
    }
  }
  if (objective === "clicks" && !trimmedMaxBid) {
    return 'Click campaign ad groups must set "max_bid".';
  }
  if (objective === "conversions" && !trimmedMaxBid) {
    return 'Conversion campaign ad groups must set "max_bid".';
  }
  if (objective === "views" && trimmedMaxBid) {
    return 'View campaign ad groups must leave "max_bid" empty.';
  }
  return undefined;
}

export function validateAdGroupContextHints(value: string): AdsManagerValidationError {
  if (value.length > ADS_MANAGER_CONTEXT_HINTS_MAX_LENGTH) {
    return `Enter context hints with no more than ${ADS_MANAGER_CONTEXT_HINTS_MAX_LENGTH} characters.`;
  }
  return undefined;
}

export function validateAdGroupContextHintsForUpload(
  value: string | undefined,
): AdsManagerValidationError {
  const lengthError = validateAdGroupContextHints(value ?? "");
  if (lengthError != null) {
    return lengthError;
  }
  return validateJsonStringArray(value);
}

export function parseContextHints(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

export function validateOptionalWebsiteUrl(value: string): AdsManagerValidationError {
  if (value.length > ADS_MANAGER_WEBSITE_URL_MAX_LENGTH) {
    return `Enter a website URL with no more than ${ADS_MANAGER_WEBSITE_URL_MAX_LENGTH} characters.`;
  }
  if (value.trim().length === 0) {
    return undefined;
  }
  return validateWebsiteUrl(value, {
    required: false,
    allowDefaultHttps: true,
    reservedQueryParamNames: RESERVED_AD_DESTINATION_QUERY_PARAM_NAMES,
    errorMessage: "Enter a valid http(s) website URL.",
  });
}

export function validateAdDestinationUrl(
  value: string,
  options: { required?: boolean; allowDefaultHttps?: boolean } = {},
): AdsManagerValidationError {
  if (!value.trim()) {
    return options.required === true ? "Enter a target URL." : undefined;
  }
  return validateWebsiteUrl(value, {
    required: options.required,
    allowDefaultHttps: options.allowDefaultHttps === true,
    maxLength: ADS_MANAGER_AD_DESTINATION_URL_MAX_LENGTH,
    reservedQueryParamNames: RESERVED_AD_DESTINATION_QUERY_PARAM_NAMES,
    errorMessage: "Enter a valid URL.",
  });
}

export function validateImageUrl(value: string, label = "Image URL"): AdsManagerValidationError {
  if (!value.trim()) {
    return undefined;
  }
  return validateWebsiteUrl(value, {
    required: false,
    allowDefaultHttps: false,
    errorMessage: `Enter a valid ${label.toLowerCase()}.`,
  });
}

export function validateAdImageUrl(value: string): AdsManagerValidationError {
  const imageUrlError = validateImageUrl(value, "image URL");
  if (imageUrlError != null) {
    return imageUrlError;
  }
  return validateAdImageUrlReference(value);
}

export function validateWebsiteUrl(
  value: string,
  {
    required = false,
    allowDefaultHttps = false,
    reservedQueryParamNames,
    maxLength = ADS_MANAGER_WEBSITE_URL_MAX_LENGTH,
    errorMessage,
  }: {
    required?: boolean;
    allowDefaultHttps?: boolean;
    reservedQueryParamNames?: Set<string>;
    maxLength?: number;
    errorMessage: string;
  },
): AdsManagerValidationError {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return required ? errorMessage : undefined;
  }
  if (!allowDefaultHttps && !/^https?:\/\//i.test(trimmedValue)) {
    return errorMessage;
  }

  let url: URL;
  let normalizedValue: string;
  try {
    normalizedValue =
      allowDefaultHttps ? normalizeWebsiteUrlWithDefaultHttps(trimmedValue) : trimmedValue;
    url = new URL(normalizedValue);
  } catch {
    return errorMessage;
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return errorMessage;
  }
  if (!hasValidUrlHostname(url)) {
    return errorMessage;
  }
  if (url.username || url.password) {
    return errorMessage;
  }
  if (normalizedValue.length > maxLength) {
    return `Enter a website URL with no more than ${maxLength} characters.`;
  }

  const queryError = validateUrlQueryString(url, reservedQueryParamNames);
  if (queryError != null) {
    return queryError;
  }
  return undefined;
}

function validateUrlQueryString(
  url: URL,
  reservedQueryParamNames: Set<string> | undefined,
): AdsManagerValidationError {
  const rawQuery = url.search.startsWith("?") ? url.search.slice(1) : url.search;
  if (rawQuery.length === 0) {
    return undefined;
  }

  const queryFields = rawQuery.split("&");
  if (queryFields.some((field) => field.length === 0 || !field.includes("="))) {
    return "Enter a website URL with a valid query string.";
  }
  if (reservedQueryParamNames == null) {
    return undefined;
  }

  const reservedParamNames = Array.from(
    new Set(
      Array.from(new URLSearchParams(rawQuery).keys()).filter((name) =>
        reservedQueryParamNames.has(name.toLowerCase()),
      ),
    ),
  ).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));

  if (reservedParamNames.length === 0) {
    return undefined;
  }
  const label = reservedParamNames.length === 1 ? "parameter" : "parameters";
  return `Remove reserved query ${label} from the website URL: ${reservedParamNames.join(", ")}.`;
}

export function validateProductFeedId(value: string): AdsManagerValidationError {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return "Enter a product feed id.";
  }
  return undefined;
}

export function validateProductSetFilters(
  filters: ProductSetFilterValidationInput[],
): AdsManagerValidationError {
  const invalidFilter = filters.find((filter) => validateProductSetFilter(filter) != null);
  return invalidFilter == null ? undefined : (
      validateProductSetFilter(invalidFilter) ??
        "Complete each product filter with a field and at least one value."
    );
}

export function validateProductSetFilter(
  filter: ProductSetFilterValidationInput,
): AdsManagerValidationError {
  if (!filter.field.trim()) {
    return "Complete each product filter with a field and at least one value.";
  }

  const values = getProductSetFilterValues(filter);
  if (values.length === 0) {
    return "Complete each product filter with a field and at least one value.";
  }
  if (SINGLE_TEXT_PRODUCT_SET_FILTER_OPERATORS.has(filter.operator)) {
    if (values.length !== 1 || values[0].trim().length === 0) {
      return "Enter one non-empty text value for contains, does not contain, and starts with filters.";
    }
    return undefined;
  }
  if (!NUMERIC_PRODUCT_SET_FILTER_OPERATORS.has(filter.operator)) {
    return undefined;
  }
  if (values.length !== 1 || parseProductSetNumericFilterValue(String(values[0])) == null) {
    return "Enter one finite numeric value for numeric product filters.";
  }
  return undefined;
}

export function getProductSetFilterValues(filter: ProductSetFilterValidationInput): string[] {
  if (filter.values != null && filter.values.length > 0) {
    return filter.values;
  }
  return parseCommaSeparatedValues(filter.valuesString ?? "");
}

export function parseProductSetNumericFilterValue(value: string): number | null {
  const normalizedValue = value.trim();
  if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalizedValue)) {
    return null;
  }
  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? parsed : null;
}

export function validateCustomAudienceBidMultipliers(
  rows: CustomAudienceBidMultiplierValidationInput[],
): AdsManagerValidationError {
  const seenAudienceIds = new Set<string>();
  for (const row of rows) {
    if (!row.customAudienceId) {
      return "Choose an audience for each multiplier.";
    }
    if (seenAudienceIds.has(row.customAudienceId)) {
      return "Choose each audience only once.";
    }
    seenAudienceIds.add(row.customAudienceId);

    const multiplierMicros = parseBidMultiplierMicros(row.bidMultiplier);
    if (multiplierMicros == null) {
      return "Enter a valid multiplier for each audience.";
    }
    if (
      multiplierMicros < ADS_MANAGER_MIN_BID_MULTIPLIER_MICROS ||
      multiplierMicros > ADS_MANAGER_MAX_BID_MULTIPLIER_MICROS
    ) {
      return "Enter multipliers between 0.1x and 10x.";
    }
  }
  return undefined;
}

export function parseBidMultiplierMicros(value: string): number | null {
  const multiplier = Number(value);
  if (!Number.isFinite(multiplier)) {
    return null;
  }
  return Math.round(multiplier * ADS_MANAGER_BID_MULTIPLIER_MICROS_PER_UNIT);
}

export function validateAdTitle(
  value: string,
  options: LegacyValueValidationOptions & { isProductFeedCreative?: boolean } = {},
): AdsManagerValidationError {
  const trimmedValue = value.trim();
  const maxLength =
    options.isProductFeedCreative === true ?
      ADS_MANAGER_PRODUCT_FEED_AD_HEADLINE_MAX_LENGTH
    : ADS_MANAGER_AD_HEADLINE_MAX_LENGTH;
  const label = options.isProductFeedCreative === true ? "headline template" : "ad title";
  const article = getIndefiniteArticle(label);

  if (!trimmedValue) {
    return `Enter ${article} ${label}.`;
  }
  if (value.length > maxLength) {
    return `${capitalize(label)} must be ${maxLength} characters or fewer.`;
  }
  if (
    trimmedValue.length < ADS_MANAGER_ENTITY_NAME_MIN_LENGTH &&
    !isAllowedUnchangedLegacyValue(trimmedValue, options)
  ) {
    return `Enter ${article} ${label} with at least ${ADS_MANAGER_ENTITY_NAME_MIN_LENGTH} characters.`;
  }
  return undefined;
}

export function validateAdDescription(
  value: string,
  options: { requireMinimumLength?: boolean } = {},
): AdsManagerValidationError {
  const trimmedValue = value.trim();
  if (value.length > ADS_MANAGER_AD_DESCRIPTION_MAX_LENGTH) {
    return `Description must be ${ADS_MANAGER_AD_DESCRIPTION_MAX_LENGTH} characters or fewer.`;
  }
  if (
    options.requireMinimumLength === true &&
    trimmedValue.length < ADS_MANAGER_ENTITY_NAME_MIN_LENGTH
  ) {
    return `Enter a description with at least ${ADS_MANAGER_ENTITY_NAME_MIN_LENGTH} characters.`;
  }
  return undefined;
}

export function validateAdImageRequirement({
  image,
  imageUrl,
  imageFileId,
  required = false,
}: {
  image?: { file?: { size: number; name?: string; type?: string } } | null;
  imageUrl?: string | null;
  imageFileId?: string | null;
  required?: boolean;
}): AdsManagerValidationError {
  const hasImageReference = image != null || Boolean(imageUrl?.trim() || imageFileId?.trim());
  if (required && !hasImageReference) {
    return "Upload an image.";
  }
  const imageFileError = validateAdImageFileOrUrl({ file: image?.file, imageUrl });
  if (imageFileError != null) {
    return imageFileError;
  }
  if (image?.file != null && image.file.size > ADS_MANAGER_AD_IMAGE_MAX_SIZE_BYTES) {
    return "Image size must be less than 10MB.";
  }
  return undefined;
}

export function validateAdImageFile(
  file: { name?: string; type?: string } | null | undefined,
): AdsManagerValidationError {
  if (isGifAdImageReference({ mimeType: file?.type, source: file?.name })) {
    return GIF_AD_IMAGE_ERROR;
  }
  return undefined;
}

export function validateAdImageUrlReference(
  imageUrl: string | null | undefined,
): AdsManagerValidationError {
  if (isGifAdImageReference({ source: imageUrl })) {
    return GIF_AD_IMAGE_ERROR;
  }
  return undefined;
}

export function validateAdImageFileOrUrl({
  file,
  imageUrl,
}: {
  file?: { name?: string; type?: string } | null;
  imageUrl?: string | null;
}): AdsManagerValidationError {
  return validateAdImageFile(file) ?? validateAdImageUrlReference(imageUrl);
}

function isGifAdImageReference({
  mimeType,
  source,
}: {
  mimeType?: string | null;
  source?: string | null;
}): boolean {
  if (mimeType?.trim().toLowerCase() === "image/gif") {
    return true;
  }

  const trimmedSource = source?.trim();
  if (!trimmedSource) {
    return false;
  }

  const lowerSource = trimmedSource.toLowerCase();
  if (lowerSource.startsWith("data:image/gif")) {
    return true;
  }

  try {
    return new URL(trimmedSource).pathname.toLowerCase().endsWith(".gif");
  } catch (_) {
    return lowerSource.endsWith(".gif");
  }
}

export function validateAdImageSource({
  imageUrl,
  imageFileId,
}: {
  imageUrl?: string | null;
  imageFileId?: string | null;
}): AdsManagerValidationError {
  const hasImageUrl = Boolean(imageUrl?.trim());
  const hasImageFileId = Boolean(imageFileId?.trim());
  if (hasImageUrl && hasImageFileId) {
    return "Set exactly one of image_url or image_file_id.";
  }
  return undefined;
}

export function validateAdUpsertCreativePatch({
  adId,
  title,
  copy,
  link,
  imageUrl,
  imageFileId,
}: {
  adId?: string | null;
  title?: string | null;
  copy?: string | null;
  link?: string | null;
  imageUrl?: string | null;
  imageFileId?: string | null;
}): AdsManagerValidationError {
  const hasAdId = Boolean(adId?.trim());
  const hasTitle = Boolean(title?.trim());
  const hasCopy = Boolean(copy?.trim());
  const hasLink = Boolean(link?.trim());
  const hasImageUrl = Boolean(imageUrl?.trim());
  const hasImageFileId = Boolean(imageFileId?.trim());
  if (!hasAdId && !hasImageUrl && !hasImageFileId) {
    return "Provide image_url or image_file_id for new ads.";
  }
  if (hasImageUrl && hasImageFileId) {
    return "Set exactly one of image_url or image_file_id.";
  }

  const hasAnyCreativePatch = Boolean(
    title?.trim() || copy?.trim() || link?.trim() || imageUrl?.trim() || imageFileId?.trim(),
  );
  const hasExactlyOneImageSource = hasImageUrl !== hasImageFileId;
  const hasCompleteCreativePatch = hasTitle && hasCopy && hasLink && hasExactlyOneImageSource;
  if (hasAdId && hasAnyCreativePatch && !hasCompleteCreativePatch) {
    return "Partial creative updates must include title, copy, link, and exactly one of image_url or image_file_id together.";
  }
  return undefined;
}

export function validateParentSelection({
  value,
  availableValues,
  missingMessage,
  unavailableMessage,
}: {
  value: string | null | undefined;
  availableValues?: string[];
  missingMessage: string;
  unavailableMessage: string;
}): AdsManagerValidationError {
  const trimmedValue = value?.trim() ?? "";
  if (!trimmedValue) {
    return missingMessage;
  }
  if (availableValues != null && !availableValues.includes(trimmedValue)) {
    return unavailableMessage;
  }
  return undefined;
}

export function validateRequiredNumericString(
  value: string | undefined,
): AdsManagerValidationError {
  if (value == null || value.trim().length === 0 || !Number.isFinite(Number(value))) {
    return "This field is required and must be a valid number.";
  }
  return undefined;
}

export function validateJsonStringArray(value: string | undefined): AdsManagerValidationError {
  if (value == null || value.trim().length === 0) {
    return undefined;
  }
  const normalizedValue = normalizeSmartDoubleQuotes(value);
  try {
    const parsed = JSON.parse(normalizedValue);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
      return undefined;
    }
  } catch {
    return JSON_STRING_ARRAY_ERROR;
  }
  return JSON_STRING_ARRAY_ERROR;
}

export function validateTargetCountriesString(
  value: string | undefined,
): AdsManagerValidationError {
  if (value == null || value.trim().length === 0) {
    return undefined;
  }
  const normalizedValue = normalizeSmartDoubleQuotes(value).trim();
  if (normalizedValue.toUpperCase() === ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE) {
    return undefined;
  }

  let parsedCountries: unknown;
  try {
    parsedCountries = JSON.parse(normalizedValue);
  } catch {
    return TARGET_COUNTRIES_JSON_ERROR;
  }

  if (
    !Array.isArray(parsedCountries) ||
    !parsedCountries.every((country) => typeof country === "string")
  ) {
    return TARGET_COUNTRIES_JSON_ERROR;
  }

  for (const rawCountry of parsedCountries) {
    const country = rawCountry.trim().toUpperCase();
    if (!country) {
      return "Target countries cannot contain empty values.";
    }
    if (country === ALL_GEO_TARGETING_COUNTRIES_OPTION_VALUE) {
      return undefined;
    }
    if (!isAdsBillingCountryCode(country)) {
      return TARGET_COUNTRIES_JSON_ERROR;
    }
  }
  return undefined;
}

export function parseCommaSeparatedValues(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeSmartDoubleQuotes(value: string): string {
  return value.replace(/[“”]/g, '"');
}

function addError<Field extends string>(
  errors: AdsManagerValidationErrors<Field>,
  field: Field,
  error: AdsManagerValidationError,
) {
  if (error != null) {
    errors[field] = error;
  }
}

function getSelectedValidationFields<Field extends string>(
  allFields: readonly Field[],
  options: {
    mode?: AdsManagerValidationMode;
    fields?: AdsManagerValidationFieldSelection<Field>;
  },
): ReadonlySet<Field> {
  const { fields, mode } = options;
  if (fields == null && isPartialValidationMode(mode)) {
    throw new Error("Pass fields when validating Ads Manager bulk edit or upload inputs.");
  }
  return new Set(fields == null || fields === "all" ? allFields : fields);
}

function getValidationMode({
  mode,
}: {
  mode?: AdsManagerValidationMode;
}): AdsManagerValidationMode {
  return mode ?? "edit";
}

function isPartialValidationMode(
  mode: AdsManagerValidationMode | undefined,
): mode is AdsManagerPartialValidationMode {
  return mode === "bulkEdit" || mode === "upload";
}

function isCreationValidation({
  mode,
  isCreation,
}: {
  mode?: AdsManagerValidationMode;
  isCreation?: boolean;
}): boolean {
  return mode === "create" || isCreation === true;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getIndefiniteArticle(label: string): "a" | "an" {
  return /^[aeiou]/i.test(label) ? "an" : "a";
}
